import React, { Fragment, useState, useEffect, useRef, useMemo } from "react";
import { Row, Col, Button, Modal, Form, Select, Switch, Drawer, Spin, message } from "antd";
import {
  UserOutlined,
  InfoCircleOutlined,
  BankOutlined,
  EnvironmentOutlined,
  CreditCardOutlined,
  FolderOpenOutlined,
  EyeOutlined,
  UploadOutlined,
  FileOutlined,
} from "@ant-design/icons";
import { Scrollbars } from "react-custom-scrollbars";
import closeX from "../../../../assets/images/close-x.svg";
import db from "../../../../database";
import debounce from "lodash/debounce";
import { getB2BCustomers, validateGSTNumber, validatePANNo, getLoyaltyInfo } from "../../../../services/index";
import { getCustomer } from "../customer";
import useAuthStore from "../../../../stores/useAuthStore";
import env from "../../../../config/env";
import { getOAuthHeaders } from "../../../../constants/oAuthValidation";
import useReferenceStore from "../../../../stores/useReferenceStore";
import useCartStore from "../../../../stores/useCartStore";
import useTransactionStore from "../../../../stores/useTransactionStore";

import {
  toCamelCase,
  normalizeStaticSelectValue,
  mapB2BDataDynamic,
  fillAddressFields as fillAddressFieldsUtil,
  mapGSTToFormValuesDynamic as mapGSTUtil,
  mapApiResponseToFormFields as mapApiUtil,
  buildGroupedFields,
  formatGroupKeysToCamelCase,
} from "./customerFormUtils";
import B2BCustomerSearch from "./B2BCustomerSearch";
import CustomerOperationalToggles from "./CustomerOperationalToggles";
import CustomerTabBar from "./CustomerTabBar";
import CustomerFormField from "./CustomerFormField";
import LegacyEditCustomerModal from "./LegacyEditCustomerModal";
import CustomerCouponTab from "./CustomerCouponTab";
import useDocumentViewer from "./useDocumentViewer";

const { Option } = Select;

const EditCustomerDrawer = (props) => {
  const {
    displayEditOldCustomer,
    setDisplayEditOldCustomer,
    editFlag,
    setEditFlag,
    editOldCustomer,
    closeCustomerSearch,
    form,
    customerForm,
    cart,
    selectedEditOldCustomer,
    setSelectedEditOldCustomer,
    customerSearchInput,
    setCustomerSearchInput,
    setCustomerSearchResults,
    retailCustomerTabs,
    retailCustomerFields,
    retailCustomerGroups,
    retailCustomerRules,
    retailCustomerb2bSettings,
    retailCustomerApiDataSources,
    activeTab,
    setActiveTab,
    activeTabName,
    setActiveTabName,
    isB2BEnabled,
    setIsB2BEnabled,
    formDataCache,
    setFormDataCache,
    expandedGroups,
    setExpandedGroups,
    switchToggle,
    setSwitchToggle,
    isViewMode,
    setIsViewMode,
    toCamelCaseMetaData,
    convertMetaDataToKeyValue,
    isSubmittingCustomer,
    setIsSubmittingCustomer,
    lastSearchedValue,
    setLastSearchedValue,
    selectedB2BCustomer,
    setSelectedB2BCustomer,
    posConfig,
    refs,
    setKeyboardInputFields,
    keyboardInputFields,
    keyboard,
    setOpen,
    open,
    setInputName,
    inputName,
    customerConfigData,
    setKioskUI,
    kioskLogin,
    loading,
    setLoading,
    isPanCustomer,
    setIsPanCustomer,
    panCustomerInfo,
    setPanCustomerInfo,
    isNriBilling,
    setIsNriBilling,
    isCreditSale,
    setIsCreditSale,
    retailCustomerOperationalSettings,
    displayAddNewCustomer,
    setDisplayAddNewCustomer,
    pendingRefundCustomer,
    setPendingRefundCustomer,
    isRefundCustomerEdit,
    setIsRefundCustomerEdit,
    searchInputRef,
    setCustomerLoading,
    customerLoading,
    gstInfo,
    setGstInfo,
    selectedInvoiceAddress,
    setSelectedInvoiceAddress,
    selectedDeliveryAddress,
    setSelectedDeliveryAddress,
    processCouponApplication,
    setAdditionalInfo,
    setShowCouponOtpPopup,
    setCouponVerificationData,
    documents,
    setDocuments,
  } = props;

  const tillData = useAuthStore((s) => s.tillData);
  const currenciesList = tillData.tillAccess.csBunit.currencies;
  const tillaccess = JSON.parse(tillData?.tillAccess?.userAccessController || "{}");
  const tillLayout = parseInt(tillaccess?.layout === null || undefined ? 1 : tillaccess?.layout);

  // Internal state
  const [loyalityType, setLoyalityType] = useState([]);
  const [enableLoyality, setEnableLoyality] = useState(true);
  const [optionsMap, setOptionsMap] = useState({});
  const [b2bSearchResults, setB2bSearchResults] = useState([]);
  const [b2bLoading, setB2bLoading] = useState(false);
  const [invoiceAddressList, setInvoiceAddressList] = useState([]);
  const [deliveryAddressList, setDeliveryAddressList] = useState([]);
  const [searchByName, setSearchByName] = useState(false);
  const [noB2BResults, setNoB2BResults] = useState(false);
  const [showB2BDropdown, setShowB2BDropdown] = useState(false);
  const [isPanUpdateMode, setIsPanUpdateMode] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [updatingCustomer, setUpdatingCustomer] = useState(false);
  const { openDocument, docViewer, closeDocViewer } = useDocumentViewer();
  const updateInFlightRef = useRef(false);
  // Synchronous guard for handleApiEnterValidation — see the note there.
  const panValidationInFlightRef = useRef(false);
  // Tracks exactly which form keys the last PAN auto-fill touched, plus
  // their pre-validation values, so disabling the PAN toggle can roll
  // those specific fields back instead of guessing by key name.
  const panAutoFillRef = useRef({ keys: [], snapshot: {} });
  useEffect(() => {
    const customerData = selectedEditOldCustomer || cart?.customer;
    const docs = [];

    const customerDocuments = customerData?.metaData?.find((m) => m.key === "CUSTOMER_DOCUMENT_URLS")?.value;
    if (customerDocuments && customerDocuments !== "[]" && customerDocuments !== "") {
      try {
        const parsedDocs = JSON.parse(customerDocuments);
        parsedDocs.forEach((url, index) => {
          docs.push({ uid: index, name: `Document ${index + 1}`, status: "done", url, docUrl: url });
        });
      } catch (err) {
        console.error("Failed to parse customer documents", err);
      }
    }

    const panCardUrl = customerData?.metaData?.find((m) => m.key === "PAN_CARD_URL")?.value || customerData?.pANCARDURL;
    if (panCardUrl && panCardUrl.trim()) {
      docs.push({ uid: "pan-card", docName: "PAN Card", name: "PAN Card", status: "done", url: panCardUrl.trim(), docUrl: panCardUrl.trim() });
    }

    const passportUrl = customerData?.metaData?.find((m) => m.key === "PASSPORT_URL")?.value;
    if (passportUrl && passportUrl.trim()) {
      docs.push({ uid: "passport", docName: "Passport", name: "Passport", status: "done", url: passportUrl.trim(), docUrl: passportUrl.trim() });
    }

    setDocuments(docs);
  }, [selectedEditOldCustomer, displayEditOldCustomer]);
  const latestSearch = useRef("");
  const abortController = useRef(null);
  const searchContainerRef = useRef(null);

  const showVirtualKeyboard = posConfig?.showKeyboard === "Y" ? true : false;

  const iconMap = {
    user: <UserOutlined />,
    info: <InfoCircleOutlined />,
    building: <BankOutlined />,
    location: <EnvironmentOutlined />,
    payment: <CreditCardOutlined />,
    folder: <FolderOpenOutlined />,
  };

  const exchangeCart = useCartStore((s) => s.exchangeCart);
  const activeCart = useTransactionStore((s) => s.selectedReturnType) === "EXCHANGE" && exchangeCart?.items?.length > 0 ? exchangeCart : cart;
  const panRule = customerConfigData?.conditionalRules?.find((rule) => rule.action === "Request PAN");
  const isPanMandatoryNow = !isNriBilling && panRule && Number(activeCart?.total) > Number(panRule.value);
  const enteredPAN = activeCart?.customer?.panCard || activeCart?.customer?.metaData?.find((m) => m.key?.toLowerCase().startsWith("pan"))?.value;

  // Load loyalty data on mount
  useEffect(() => {
    const run = async () => {
      let loyaliryData = await db.loyalityData.toArray();
      loyaliryData.map((item) => {
        if (item.isDefault === "Y") {
          setLoyalityType([item]);
        }
      });
    };
    run();
  }, []);

  useEffect(() => {
    customerForm.getFieldsValue();
  }, [displayAddNewCustomer, displayEditOldCustomer]);

  // BUG-2114 (RC-1): removed a second, redundant hydration effect that used to
  // re-derive isPanCustomer/isNriBilling/isCreditSale from raw localStorage
  // here, duplicating (and disagreeing with) the customer-record-aware
  // hydration already done in useCustomerOperations.js's own effect on the
  // same displayEditOldCustomer trigger. It only "worked" by coincidence of
  // React's child-before-parent effect ordering letting the parent's effect
  // overwrite it every time -- a second source of truth for the same state is
  // not a safe pattern to keep around. isPanCustomer/isNriBilling/isCreditSale
  // arrive here as props from that single, corrected source.

  const groupedFields = useMemo(
    () => buildGroupedFields(retailCustomerGroups, retailCustomerFields, activeTab, activeTabName, isB2BEnabled),
    [retailCustomerGroups, retailCustomerFields, activeTab, activeTabName, isB2BEnabled]
  );

  const renderThumb = ({ style, ...props }) => {
    const thumbStyle = { backgroundColor: "#c1c1c1", borderRadius: "5px", width: "8px" };
    return <div style={{ ...style, ...thumbStyle }} {...props} />;
  };

  const renderThumbHorizontalScroll = ({ style, ...props }) => {
    const thumbStyle = { width: "0px" };
    return <div style={{ ...style, ...thumbStyle }} {...props} />;
  };

  const copyInvoiceToDelivery = () => {
    const allValues = customerForm.getFieldsValue(true);
    const businessGroups = groupedFields.filter((g) => g.groupName?.toLowerCase().includes("invoic") || g.groupName?.toLowerCase().includes("deliver"));
    const invoiceGroup = businessGroups.find((g) => g.groupName?.toLowerCase().includes("invoic"));
    const deliveryGroup = businessGroups.find((g) => g.groupName?.toLowerCase().includes("deliver"));
    if (!invoiceGroup || !deliveryGroup) return;
    const invoiceData = allValues[invoiceGroup.groupId] || {};
    const copiedValues = { [deliveryGroup.groupId]: { ...invoiceData } };
    customerForm.setFieldsValue(copiedValues);
    setFormDataCache((prev) => ({
      ...prev,
      [activeTab]: { ...(prev[activeTab] || {}), ...copiedValues },
    }));
  };

  const onSwitchChange = (checked) => {
    form.setFieldsValue({ loyality: checked });
    setKeyboardInputFields({ ...keyboardInputFields, loyalty: checked });
    setEnableLoyality(checked);
  };

  const editCustomer = () => {
    form
      .validateFields()
      .then(async (values) => {
        // BUG-2114 (RC-3): await the update instead of firing it and moving on
        // immediately -- same missing-await defect as handleUpdateCustomer
        // below, in this legacy-form submit path. Without the await, the
        // update's own store write of the corrected B2B status can still be
        // in flight while the cashier is free to proceed to checkout, which
        // would snapshot the order against the pre-update, stale value.
        await editOldCustomer(values);
        if (searchInputRef?.current) {
          searchInputRef.current.focus();
        }
      })
      .catch((errorInfo) => {
      });
  };

  const onFocusInput = (fieldName) => {
    setInputName(fieldName);
    setOpen(true);
    setTimeout(() => {
      const currentInputValue = keyboardInputFields[fieldName] || "";
      keyboard?.current?.setInput(currentInputValue);
    }, 300);
  };

  const onChangeInput = (event) => {
    const inputVal = event.target.value;
    setKeyboardInputFields({ ...keyboardInputFields, [inputName]: inputVal });
    keyboard?.current?.setInput(inputVal);
  };

  const getInputValue = (name) => keyboardInputFields[name] || "";

  const handleChange = (e, field, groupId) => {
    const value = e?.target?.value ?? e;
    // const fieldKey = toCamelCase(field.name);
    const fieldKey = toCamelCase(field.name.trim());
    const groupKey = groupId ? String(groupId).toLowerCase() : null;
    if (groupKey) {
      customerForm.setFieldsValue({
        [groupKey]: {
          ...customerForm.getFieldValue(groupKey),
          [fieldKey]: value,
        },
      });
    } else {
      customerForm.setFieldsValue({
        [fieldKey]: value,
      });
    }
    setFormDataCache((prev) => {
      const tabData = prev[activeTab] || {};
      let newTabData;
      if (!groupKey) {
        newTabData = { ...tabData, [fieldKey]: value };
      } else {
        newTabData = { ...tabData, [groupKey]: { ...(tabData[groupKey] || {}), [fieldKey]: value } };
      }
      const groupData = groupKey ? newTabData[groupKey] || {} : newTabData;
      const firstName = groupData[toCamelCase("First Name")] || "";
      const middleName = groupData[toCamelCase("Middle Name")] || "";
      const lastName = groupData[toCamelCase("Last Name")] || "";

      if (field.name === "First Name" || field.name === "Middle Name" || field.name === "Last Name") {
        const fullName = [firstName, middleName, lastName].filter(Boolean).join(" ");
        if (groupKey) {
          customerForm.setFieldsValue({ [groupKey]: { ...customerForm.getFieldValue(groupKey), [toCamelCase("Name")]: fullName } });
          newTabData = { ...newTabData, [groupKey]: { ...(newTabData[groupKey] || {}), [toCamelCase("Name")]: fullName } };
        } else {
          customerForm.setFieldsValue({ [toCamelCase("Name")]: fullName });
          newTabData = { ...newTabData, [toCamelCase("Name")]: fullName };
        }
        setKeyboardInputFields((prevKeys) => ({ ...prevKeys, [toCamelCase("Name")]: fullName }));
      }
      return { ...prev, [activeTab]: newTabData };
    });
  };

  const searchB2BCustomers = useMemo(
    () =>
      debounce(async (value) => {
        latestSearch.current = value;
        if (!value.trim()) {
          if (abortController.current) abortController.current.abort();
          setB2bSearchResults([]);
          setB2bLoading(false);
          return;
        }
        if (abortController.current) abortController.current.abort();
        const controller = new AbortController();
        abortController.current = controller;
        setB2bLoading(true);
        setB2bSearchResults([]);
        try {
          const queryValue = searchByName ? value.trim() : value.trim().toUpperCase();
          const resp = await getB2BCustomers({ q: queryValue, startRow: "0", endRow: "20" }, { signal: controller.signal });
          const customers = resp?.data?.getB2BCustomerList?.customers || [];
          if (latestSearch.current !== value) return;
          setB2bSearchResults(customers);
          setNoB2BResults(customers.length === 0);
          if (customers.length === 0) {
            const trimmedValue = value.trim();
            if (!trimmedValue) return;
            const autoFillValues = {};
            groupedFields.forEach((group) => {
              group.fields.forEach((field) => {
                const fieldName = field.name.toLowerCase();
                if (searchByName && fieldName === "company name") {
                  if (group.groupId) {
                    autoFillValues[group.groupId] = { ...(autoFillValues[group.groupId] || {}), [field.name]: trimmedValue };
                  } else {
                    autoFillValues[field.name] = trimmedValue;
                  }
                }
                if (!searchByName && fieldName.includes("gst")) {
                  if (group.groupId) {
                    autoFillValues[group.groupId] = {
                      ...(autoFillValues[group.groupId] || {}),
                      [field.name]: trimmedValue.toUpperCase(),
                    };
                  } else {
                    autoFillValues[field.name] = trimmedValue.toUpperCase();
                  }
                }
              });
            });
            customerForm.setFieldsValues(autoFillValues);
            setFormDataCache((prev) => ({ ...prev, [activeTab]: { ...(prev[activeTab] || {}), ...autoFillValues } }));
          }
        } catch (err) {
          if (err.name !== "AbortError") console.error("SEARCH ERROR ->", err);
        } finally {
          setB2bLoading(false);
        }
      }, 300),
    [searchByName]
  );

  // Wrap fillAddressFields util to bind local state setters
  const fillAddressFields = (address, groupOrId, type, customer) => {
    fillAddressFieldsUtil(address, groupOrId, type, customer, customerForm, activeTab, setFormDataCache);
  };

  const mapGSTToFormValuesDynamic = (gstApiResponse, gFields) => mapGSTUtil(gstApiResponse, gFields, toCamelCase, normalizeStaticSelectValue);

  const mapApiResponseToFormFields = (apiData, gFields) => mapApiUtil(apiData, gFields, toCamelCase, normalizeStaticSelectValue);

  // Effects for expanded groups
  useEffect(() => {
    const initState = {};
    groupedFields.forEach((group) => {
      if (group.collapsible) initState[group.groupId] = false;
    });
    setExpandedGroups(initState);
  }, [activeTab]);

  // Options map initialization
  useEffect(() => {
    const countries = useReferenceStore.getState().countriesData || [];
    const states = useReferenceStore.getState().statesData || [];
    const loyaltyData = JSON.parse(localStorage.getItem("loyaltyData")) || {};
    const defaultCountryId = countries.find((c) => c.Name === "India")?.RecordID;
    const defaultStateId = tillData?.tillAccess?.csBunit?.csRegionId;
    const loyaltyLevels = loyaltyData?.loyaltyLevels || [];
    const mappedLoyaltyOptions = loyaltyLevels.map((item) => ({ RecordID: item.cwr_loyalty_level_id, Name: item.name })) || [];
    const defaultLoyaltyId = loyaltyLevels.length > 0 ? loyaltyLevels[0]?.cwr_loyalty_level_id : undefined;
    const currentValues = customerForm.getFieldsValue(true);
    const updatedValues = { ...currentValues };
    const countryFields = retailCustomerFields.filter((field) => field?.name?.toLowerCase() === "country");
    const stateFields = retailCustomerFields.filter((field) => field?.name?.toLowerCase() === "state");
    const loyaltyFields = retailCustomerFields.filter((field) => field?.name?.toLowerCase() === "loyalty level");

    countryFields.forEach((field) => {
      const fieldKey = toCamelCase(field.name);
      const gId = field.groupId ? String(field.groupId).toLowerCase() : null;
      if (gId) {
        updatedValues[gId] = updatedValues[gId] || {};
        if (!updatedValues[gId][fieldKey]) updatedValues[gId][fieldKey] = defaultCountryId;
      } else {
        if (!updatedValues[fieldKey]) updatedValues[fieldKey] = defaultCountryId;
      }
    });
    stateFields.forEach((field) => {
      const fieldKey = toCamelCase(field.name);
      const gId = field.groupId ? String(field.groupId).toLowerCase() : null;
      if (!defaultStateId) return;
      if (gId) {
        updatedValues[gId] = updatedValues[gId] || {};
        if (!updatedValues[gId][fieldKey]) updatedValues[gId][fieldKey] = defaultStateId;
      } else {
        if (!updatedValues[fieldKey]) updatedValues[fieldKey] = defaultStateId;
      }
    });
    loyaltyFields.forEach((field) => {
      const fieldKey = toCamelCase(field.name);
      const gId = field.groupId ? String(field.groupId).toLowerCase() : null;
      if (!defaultLoyaltyId) return;
      if (gId) {
        updatedValues[gId] = updatedValues[gId] || {};
        if (!updatedValues[gId][fieldKey]) updatedValues[gId][fieldKey] = defaultLoyaltyId;
      } else {
        if (!updatedValues[fieldKey]) updatedValues[fieldKey] = defaultLoyaltyId;
      }
    });
    customerForm.setFieldsValue(updatedValues);
    setOptionsMap((prev) => {
      const newMap = { ...prev };
      countryFields.forEach((f) => {
        newMap[f.id] = countries;
      });
      stateFields.forEach((f) => {
        newMap[f.id] = states;
      });
      loyaltyFields.forEach((f) => {
        newMap[f.id] = mappedLoyaltyOptions;
      });
      return newMap;
    });
  }, [displayAddNewCustomer, displayEditOldCustomer]);

  const fetchOptions = async (field) => {
    if (field.dataSource?.type === "api") {
      const apiConfig = retailCustomerApiDataSources.find((api) => api.id === field.dataSource.apiSourceId);
      if (apiConfig?.name === "getStates") {
        const states = useReferenceStore.getState().statesData || [];
        setOptionsMap((prev) => ({ ...prev, [field.id]: states }));
      } else if (apiConfig?.name === "getCountries") {
        const countries = useReferenceStore.getState().countriesData || [];
        setOptionsMap((prev) => ({ ...prev, [field.id]: countries }));
      } else if (apiConfig?.name === "getLoyaltyData") {
        const loyaltyData = JSON.parse(localStorage.getItem("loyaltyData")) || {};
        if (loyaltyData?.loyaltyLevels) {
          const mappedOptions = loyaltyData?.loyaltyLevels?.map((item) => ({ RecordID: item.cwr_loyalty_level_id, Name: item.name })) || [];
          setOptionsMap((prev) => ({ ...prev, [field.id]: mappedOptions }));
        } else {
          try {
            const params = { startRow: 0, endRow: 10 };
            const d = await getLoyaltyInfo({ endpoint: apiConfig.endpoint, params });
            const mappedOptions = d?.loyaltyLevels?.map((item) => ({ RecordID: item.cwr_loyalty_level_id, Name: item.name })) || [];
            setOptionsMap((prev) => ({ ...prev, [field.id]: mappedOptions }));
          } catch (err) {
            console.error("Failed to load loyalty data", err);
          }
        }
      }
    } else if (field.dataSource?.type === "static") {
      const staticOptions = field.dataSource.staticOptions || [];
      setOptionsMap((prev) => ({ ...prev, [field.id]: staticOptions }));
    }
  };

  // B2B no results effect
  useEffect(() => {
    if (!isB2BEnabled) return;
    if (!noB2BResults) return;
    if (!lastSearchedValue?.trim()) return;
    const updatedValues = {};
    groupedFields.forEach((group) => {
      group.fields.forEach((field) => {
        const fieldName = field.name.toLowerCase();
        if (searchByName && fieldName === "company name") {
          if (group.groupId) {
            updatedValues[group.groupId] = { ...(updatedValues[group.groupId] || {}), [field.name]: lastSearchedValue };
          } else {
            updatedValues[field.name] = lastSearchedValue;
          }
        }
        if (!searchByName && fieldName.includes("gst")) {
          if (group.groupId) {
            updatedValues[group.groupId] = { ...(updatedValues[group.groupId] || {}), [field.name]: lastSearchedValue };
          } else {
            updatedValues[field.name] = lastSearchedValue;
          }
        }
      });
    });
    customerForm.setFieldsValue(updatedValues);
    setFormDataCache((prev) => ({ ...prev, [activeTab]: { ...(prev[activeTab] || {}), ...updatedValues } }));
  }, [lastSearchedValue, noB2BResults, searchByName]);

  useEffect(() => {
    if (b2bSearchResults.length > 0) setNoB2BResults(false);
  }, [b2bSearchResults]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowB2BDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleApiEnterValidation = async (e, field) => {
    if (e.key !== "Enter") return;
    // Held Enter repeats the keydown faster than React can re-render, so a
    // state-only check lets several PAN validations run at once. The ref is set
    // alongside setLoading(true) — below the early returns, so a non-matching
    // field or empty PAN can never leave it stuck true.
    if (panValidationInFlightRef.current || loading) return;
    if (field?.dataSource?.type !== "api") return;
    const apiConfig = retailCustomerApiDataSources.find((api) => api.id === field.dataSource.apiSourceId);
    if (!apiConfig || apiConfig.endpoint !== "validatePANNumber") return;
    const pan = e.target.value?.trim();
    if (!pan) return;
    panValidationInFlightRef.current = true;
    setLoading(true);
    try {
      const searchResponse = await getCustomer(
        form,
        tillLayout,
        pan,
        setKioskUI,
        setCustomerSearchResults,
        kioskLogin,
        tillData,
        pendingRefundCustomer,
        setPendingRefundCustomer,
        isRefundCustomerEdit,
        setIsRefundCustomerEdit
      );
      const existingCustomers = searchResponse?.data?.data?.customerSearch || [];
      const isExistingCustomer = existingCustomers.length > 0;
      const panValidation = await validatePANNo(pan);
      const disabledPanStatusMap = {
        N: "INVALID PAN",
        I: "PAN INOPERATIVE",
        X: "PAN DEACTIVATED",
        F: "FAKE PAN",
        D: "PAN DELETED",
        EA: "PAN AMALGAMATION",
        EC: "PAN ACQUISITION",
        ED: "DEATH OF PAN HOLDER",
        EI: "PAN DISSOLVED",
        EL: "PAN LIQUIDATED",
        EM: "PAN MERGER",
        EP: "PAN PARTITION",
        ES: "PAN SPLIT",
        EU: "PAN UNDER LIQUIDATION",
      };
      let realStatus = "INVALID PAN";
      let isPanActuallyValid = false;
      if (disabledPanStatusMap[panValidation?.panStatusCode]) {
        realStatus = disabledPanStatusMap[panValidation.panStatusCode];
        isPanActuallyValid = false;
      } else if (panValidation?.panStatusCode === "E") {
        if (
          panValidation?.aadhaarSeedingStatusCode === "Y" ||
          panValidation?.isIndividual === false ||
          panValidation?.isIndividualAPI === false ||
          panValidation?.aadhaarSeedingStatusCode === "NA" ||
          panValidation?.aadhaarSeedingStatusCode == null
        ) {
          realStatus = "VALID & OPERATIVE";
          isPanActuallyValid = true;
        } else {
          realStatus = "VALID & INOPERATIVE";
          isPanActuallyValid = true;
        }
      }
      setPanCustomerInfo({
        panNo: panValidation.panNo,
        panStatus: panValidation.panStatus,
        aadhaarSeedingStatusCode: panValidation.aadhaarSeedingStatusCode,
        isValid: isPanActuallyValid,
        realStatus,
      });
      if (!isPanActuallyValid) {
        customerForm.setFields([{ name: field.groupId ? [field.groupId, field.name] : field.name, errors: ["Invalid PAN number"] }]);
        setIsPanUpdateMode(false);
        return;
      }
      message.success("PAN validated successfully");
      setIsPanCustomer(true);
      if (isExistingCustomer) {
        setIsPanUpdateMode(true);
        setSelectedEditOldCustomer(existingCustomers[0]);
      } else {
        setIsPanUpdateMode(false);
      }
      let sourceData = isExistingCustomer ? existingCustomers[0] : panValidation;
      if (isExistingCustomer) {
        sourceData = await convertMetaDataToKeyValue(sourceData);
      }
      const mappedFormValues = await mapApiResponseToFormFields(sourceData, groupedFields);
      const autoFillPayload = {
        ...sourceData,
        mobileno: sourceData?.mobileNo,
        lastname: sourceData?.lastName,
        lastvisitdate: sourceData?.lastVisitDate,
        cardnumber: sourceData?.cardNumber,
        city: sourceData?.customerAddress?.city,
        taxID: sourceData?.taxId,
        anniversaryDate: sourceData?.anniversaryDate?.split(" ")[0],
        birthday: sourceData?.birthday?.split(" ")[0],
        validity: sourceData?.validity?.split(" ")[0],
      };
      const touchedKeys = Object.keys(autoFillPayload);
      const preSnapshot = {};
      touchedKeys.forEach((k) => {
        preSnapshot[k] = customerForm.getFieldValue(k);
      });
      panAutoFillRef.current = { keys: touchedKeys, snapshot: preSnapshot };
      customerForm.setFieldsValue(autoFillPayload);
      const isPanMobileNo = sourceData?.mobileNo;
      setPanCustomerInfo((prev) => ({ ...prev, panHasMobile: Boolean(isPanMobileNo) }));
      setFormDataCache((prev) => ({ ...prev, [activeTab]: { ...(prev[activeTab] || {}), ...mappedFormValues } }));
    } catch (err) {
      console.error("PAN validation flow failed:", err);
      setIsPanUpdateMode(false);
    } finally {
      panValidationInFlightRef.current = false;
      setLoading(false);
    }
  };

  const validatePanDirect = async (pan) => {
    if (!pan || pan.trim().length < 10) return;
    const panField = retailCustomerFields?.find(
      (f) => f.dataSource?.type === "api" && retailCustomerApiDataSources.find((a) => a.id === f.dataSource.apiSourceId && a.endpoint === "validatePANNumber")
    );
    if (!panField) return;
    await handleApiEnterValidation({ key: "Enter", target: { value: pan } }, panField);
  };

  useEffect(() => {
    if (!isPanCustomer) return;
    const allValues = customerForm.getFieldsValue(true);
    const panKey = Object.keys(allValues).find((k) => k.toLowerCase().startsWith("pan") && !k.toLowerCase().includes("file"));
    const panValue = panKey ? allValues[panKey]?.trim?.() : null;
    if (panValue && panValue.length === 10 && !panCustomerInfo) {
      validatePanDirect(panValue);
    }
  }, [isPanCustomer]);

  const clearPanFields = () => {
    const isPanKey = (k) => k.toLowerCase().startsWith("pan");
    const allValues = customerForm.getFieldsValue(true);

    // Build a targeted patch — only PAN keys, nothing else
    const patch = {};
    const logPaths = [];
    Object.entries(allValues).forEach(([key, value]) => {
      if (typeof value === "object" && value !== null) {
        const panInnerKeys = Object.keys(value).filter(isPanKey);
        if (panInnerKeys.length > 0) {
          patch[key] = {};
          panInnerKeys.forEach((inner) => {
            patch[key][inner] = "";
            logPaths.push(`${key}.${inner}`);
          });
        }
      } else if (isPanKey(key)) {
        patch[key] = "";
        logPaths.push(key);
      }
    });

    // Roll back any fields the last PAN auto-fill touched (e.g. business PAN
    // populating firstName/name with the registered company name) — restore
    // their pre-validation value rather than guessing by key name.
    const { keys: autoFilledKeys = [], snapshot: autoFillSnapshot = {} } = panAutoFillRef?.current || {};
    autoFilledKeys.forEach((k) => {
      const original = autoFillSnapshot[k];
      patch[k] = original !== undefined && original !== null ? original : "";
      logPaths.push(`${k} (auto-fill rollback)`);
    });
    panAutoFillRef.current = { keys: [], snapshot: {} };

    customerForm.setFieldsValue(patch);

    setFormDataCache((prev) => {
      const cleared = {};
      Object.entries(prev).forEach(([tabId, tabData]) => {
        const clearedTab = {};
        Object.entries(tabData || {}).forEach(([key, value]) => {
          if (typeof value === "object" && value !== null) {
            const clearedGroup = { ...value };
            Object.keys(value).forEach((innerKey) => {
              if (isPanKey(innerKey)) clearedGroup[innerKey] = "";
              else if (autoFilledKeys.includes(innerKey)) {
                const original = autoFillSnapshot[innerKey];
                clearedGroup[innerKey] = original !== undefined && original !== null ? original : "";
              }
            });
            clearedTab[key] = clearedGroup;
          } else if (isPanKey(key)) {
            clearedTab[key] = "";
          } else if (autoFilledKeys.includes(key)) {
            const original = autoFillSnapshot[key];
            clearedTab[key] = original !== undefined && original !== null ? original : "";
          } else {
            clearedTab[key] = value;
          }
        });
        cleared[tabId] = clearedTab;
      });
      return cleared;
    });
  };

  const compressImage = (file, maxWidth = 1920, quality = 0.75) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        image.src = e.target.result;
      };
      image.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        let width = image.width;
        let height = image.height;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(image, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Image compression failed"));
              return;
            }
            resolve(new File([blob], file.name, { type: file.type, lastModified: Date.now() }));
          },
          file.type,
          quality
        );
      };
      image.onerror = reject;
      reader.onerror = reject;
    });
  };

  const handleFileUpload = async (e, docName) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fileToUpload = file.size > 4 * 1024 * 1024 && file.type.startsWith("image/") ? await compressImage(file, 1920, 0.75) : file;
      const buffer = await fileToUpload.arrayBuffer();
      const fileData = Array.from(new Uint8Array(buffer));
      const { access_token } = getOAuthHeaders() || {};
      const response = await fetch(env.uploadDocumentUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: access_token },
        body: JSON.stringify({ fileName: fileToUpload.name, fileType: fileToUpload.type, fileData }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(typeof result?.error === "string" ? result.error : JSON.stringify(result?.error) || "Upload failed");
      message.success("Attachment has been uploaded successfully.");
    } catch (error) {
      console.error("Upload error:", error);
      message.error("Failed to upload document");
    }
  };

  // Shared field filter + sort for the dynamic field list
  const filterAndSortFields = (fields) =>
    fields
      .filter((field) => {
        const isCurrentTab = field.tabId === activeTab;
        const isVisible = field.visible;
        const isBusinessTab = activeTabName.toLocaleLowerCase() === "business";
        const isContactTab = activeTabName.toLocaleLowerCase() === "contact";
        const allowBusinessFields = !isBusinessTab || (isBusinessTab && isB2BEnabled);
        if (isContactTab && field.name.toLowerCase().includes("pan") && !isPanCustomer) return false;
        const fieldName = field.name?.toLowerCase().replace(/\s+/g, "");
        const isNriField = fieldName?.startsWith("passport") || fieldName?.startsWith("visa") || fieldName?.startsWith("immigration");
        const isCreditLimitField = fieldName?.startsWith("credit");
        if (isContactTab && isNriField && !isNriBilling) return false;
        if (isContactTab && isCreditLimitField && !isCreditSale) return false;
        return isCurrentTab && isVisible && allowBusinessFields;
      })
      .sort((a, b) => {
        const isContactTab = activeTabName.toLowerCase() === "contact";
        const isPanField = (f) => f.name?.toLowerCase().includes("pan");
        const isNriField = (f) => {
          const fn = f?.name?.toLowerCase();
          return fn?.startsWith("passport") || fn?.startsWith("visa") || fn?.startsWith("immigration");
        };
        const getNriPriority = (field) => {
          const name = field?.name?.toLowerCase() || "";

          if (name.startsWith("passport")) return 1; // highest priority
          if (name.startsWith("immigration") || name.startsWith("visa")) return 2;
          return 3;
        };
        const isCreditField = (f) => f?.name?.toLowerCase().replace(/\s+/g, "").startsWith("credit");
        if (isContactTab && isPanCustomer) {
          const aIsPan = isPanField(a);
          const bIsPan = isPanField(b);
          if (aIsPan && !bIsPan) return -1;
          if (!aIsPan && bIsPan) return 1;
        }
        if (isContactTab && isNriBilling) {
          return getNriPriority(a) - getNriPriority(b);
        }
        if (isContactTab && isCreditSale) {
          const aIsCredit = isCreditField(a);
          const bIsCredit = isCreditField(b);
          if (aIsCredit && !bIsCredit) return -1;
          if (!aIsCredit && bIsCredit) return 1;
        }
        return a.order - b.order;
      });

  // Build the final form data and call editOldCustomer
  const handleUpdateCustomer = async () => {
    if (updateInFlightRef.current) return;
    updateInFlightRef.current = true;
    setUpdatingCustomer(true);
    try {
      // b2c_customer.name is the only NOT NULL identity column the backend actually
      // requires (verified against the schema -- mobile/email/firstName/lastName/
      // taxId are all nullable). Ask for it directly instead of substituting
      // Business-tab data into it (removed BUG-2113/2156 fallback, which silently
      // wrote the company name into this B2C-only field to dodge this same check).
      {
        const allFieldsSnapshot = customerForm.getFieldsValue(true);
        const getFlatValue = (key) => {
          if (allFieldsSnapshot[key]) return allFieldsSnapshot[key];
          for (const [k, v] of Object.entries(allFieldsSnapshot)) {
            if (/^group_\d+$/.test(k) && v?.[key]) return v[key];
          }
          return null;
        };
        if (!getFlatValue("name")?.toString().trim()) {
          Modal.warning({
            title: "Name Required",
            content: "Please enter the customer's name to proceed.",
            okText: "OK",
            cancelButtonProps: { style: { display: "none" } },
          });
          return;
        }
      }

      try {
        await customerForm.validateFields();
      } catch (validationError) {
        return;
      }

    const allData = await customerForm.getFieldsValue(true);

    const normalizeKeys = (obj) => {
      if (!obj || typeof obj !== "object" || Array.isArray(obj)) return obj;
      for (const key of Object.keys(obj)) {
        const value = obj[key];
        if (value && typeof value === "object" && !Array.isArray(value)) normalizeKeys(value);
      }
      return obj;
    };

    const mergedData = normalizeKeys({ ...allData });
    const camelCasedData = Object.keys(mergedData).reduce((acc, key) => {
      acc[/^group_\d+$/i.test(key) ? key : toCamelCase(key)] = mergedData[key];
      return acc;
    }, {});

    groupedFields.forEach((group) => {
      group.fields.forEach((field) => {
        if (field.type === "select" && field.dataSource?.type === "static" && field.defaultValue) {
          const fieldKey = toCamelCase(field.name);
          if (camelCasedData[fieldKey]) {
            camelCasedData[fieldKey] = normalizeStaticSelectValue(camelCasedData[fieldKey], field.defaultValue);
          }
          if (group.groupId && camelCasedData[group.groupId]?.[fieldKey]) {
            camelCasedData[group.groupId][fieldKey] = normalizeStaticSelectValue(camelCasedData[group.groupId][fieldKey], field.defaultValue);
          }
        }
      });
    });

    const formattedData = formatGroupKeysToCamelCase(camelCasedData);

    if ((isPanCustomer && isB2BEnabled) && formattedData?.gstNo && formattedData?.panCard) {
      const enteredPAN = formattedData?.panCard?.trim()?.toUpperCase();
      const gstPAN = formattedData?.gstNo?.substring(2, 12)?.toUpperCase();
      if (enteredPAN !== gstPAN) {
        return new Promise((resolve) => {
          Modal.warning({
            title: "PAN & GST Mismatch",
            content: "The GST number entered does not match the provided PAN number. Please verify the GST number and enter correct details.",
            okText: "OK",
            cancelButtonProps: { style: { display: "none" } },
            onOk: () => {
              resolve(false);
            },
          });
        });
      }
    }

    // BUG-2114 (RC-2): await the update before closing the drawer / resetting
    // state. This used to fire-and-forget: the drawer closed and everything
    // looked "done" immediately while the actual save (and its store write of
    // the corrected B2B status) was still in flight over the network. If the
    // cashier reached checkout in that window, the order was snapshotted
    // against the stale, pre-update value even when the customer record
    // itself was about to be saved correctly a moment later.
    await editOldCustomer(formattedData);
    setDisplayEditOldCustomer(false);
    customerForm.resetFields();
    setIsB2BEnabled(false);
    setIsPanUpdateMode(false);
    setFormDataCache({});
    setPanCustomerInfo(null);
    setActiveTab(retailCustomerTabs.filter((tab) => tab.visible).sort((a, b) => a.order - b.order)[0]?.id || "");
    setActiveTabName(retailCustomerTabs.filter((tab) => tab.visible).sort((a, b) => a.order - b.order)[0]?.name || "");
    setExpandedGroups({});
    setIsViewMode(false);
    } finally {
      updateInFlightRef.current = false;
      setUpdatingCustomer(false);
    }
  };

  const drawerCloseHandler = () => {
    setDisplayEditOldCustomer(false);
    setCustomerSearchInput("");
    setCustomerSearchResults();
    setIsViewMode(false);
    customerForm.resetFields();
    setIsB2BEnabled(false);
    setIsPanCustomer(false);
    setIsPanUpdateMode(false);
    setFormDataCache({});
    setPanCustomerInfo(null);
    // Clear documents loaded for the edited/viewed customer so they don't leak into
    // the next customer session (the documents state is shared with the add flow).
    if (setDocuments) setDocuments([]);
    if (setGstInfo) setGstInfo(null);
    setActiveTab(retailCustomerTabs.filter((tab) => tab.visible).sort((a, b) => a.order - b.order)[0]?.id || "");
    setActiveTabName(retailCustomerTabs.filter((tab) => tab.visible).sort((a, b) => a.order - b.order)[0]?.name || "");
    setExpandedGroups({});
  };

  return (
    <Fragment>
      {/* Legacy Modal — shown when form prop is used directly (pre-dynamic-fields) */}
      <Modal
        onCancel={() => {
          form.resetFields();
          closeCustomerSearch();
          setDisplayEditOldCustomer(false);
          setEditFlag(false);
        }}
        footer={null}
        width="53%"
        closable={false}
        styles={{ body: { padding: 20, backgroundColor: "#F3F4F9" } }}
      />

      {/* Main Drawer */}
      <Drawer
        open={displayEditOldCustomer}
        placement="right"
        footer={null}
        closable={false}
        onValuesChange={() => {}}
        styles={{ body: { paddingTop: 0, backgroundColor: "#ffffff" } }}
        width="62%"
      >
        <Spin spinning={loading}>
          <div
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            {retailCustomerFields && retailCustomerFields.length > 0 ? (
              <>
                <Form key={"addCustomer"} form={customerForm} name="addCustomer">
                  <Row style={{ display: "flex", alignItems: "center", marginTop: "2.5vh", justifyContent: "center" }}>
                    <Col span={22} style={{ display: "flex", alignItems: "center" }}>
                      <UserOutlined style={{ fontSize: "20px", marginRight: "0.3em" }} />
                      <p style={{ marginBottom: 0, fontSize: "1.5em", fontWeight: 500, color: "#0F0718" }}>{isViewMode ? "View Customer" : "Edit Customer"}</p>
                    </Col>
                    <Col span={2} style={{ textAlign: "right", marginTop: "0.7vh" }}>
                      <img src={closeX} style={{ cursor: "pointer", width: "1.5vw" }} onClick={drawerCloseHandler} alt="" />
                    </Col>
                  </Row>

                  <CustomerTabBar
                    retailCustomerTabs={retailCustomerTabs}
                    activeTab={activeTab}
                    activeTabName={activeTabName}
                    setActiveTab={setActiveTab}
                    setActiveTabName={setActiveTabName}
                    customerForm={customerForm}
                    formDataCache={formDataCache}
                    setFormDataCache={setFormDataCache}
                    customerConfigData={customerConfigData}
                    isNriBilling={isNriBilling}
                    iconMap={iconMap}
                    isPanCustomer={isPanCustomer}
                    isPanUpdateMode={isPanUpdateMode}
                    retailCustomerGroups={retailCustomerGroups}
                  />

                  <Scrollbars style={{ height: "73vh" }} autoHide>
                    <div style={{ width: "99%" }}>
                      {activeTabName === "Business" && customerConfigData?.b2bSettings?.enableB2BBilling && (
                        <B2BCustomerSearch
                          isB2BEnabled={isB2BEnabled}
                          setIsB2BEnabled={setIsB2BEnabled}
                          panCustomerInfo={panCustomerInfo}
                          lastSearchedValue={lastSearchedValue}
                          setLastSearchedValue={setLastSearchedValue}
                          isViewMode={isViewMode}
                          customerConfigData={customerConfigData}
                          searchByName={searchByName}
                          setSearchByName={setSearchByName}
                          b2bLoading={b2bLoading}
                          b2bSearchResults={b2bSearchResults}
                          setB2bSearchResults={setB2bSearchResults}
                          showB2BDropdown={showB2BDropdown}
                          setShowB2BDropdown={setShowB2BDropdown}
                          searchContainerRef={searchContainerRef}
                          selectedB2BCustomer={selectedB2BCustomer}
                          setSelectedB2BCustomer={setSelectedB2BCustomer}
                          activeTab={activeTab}
                          customerForm={customerForm}
                          setFormDataCache={setFormDataCache}
                          setLoading={setLoading}
                          searchB2BCustomers={searchB2BCustomers}
                          validateGSTNumber={validateGSTNumber}
                          mapGSTToFormValuesDynamic={mapGSTToFormValuesDynamic}
                          mapB2BDataDynamic={(customer, gFields) => mapB2BDataDynamic(customer, gFields)}
                          groupedFields={groupedFields}
                          setInvoiceAddressList={setInvoiceAddressList}
                          setDeliveryAddressList={setDeliveryAddressList}
                          setSelectedInvoiceAddress={setSelectedInvoiceAddress}
                          setSelectedDeliveryAddress={setSelectedDeliveryAddress}
                          gstInfo={gstInfo}
                          setGstInfo={setGstInfo}
                          setSwitchToggle={setSwitchToggle}
                          copyInvoiceToDelivery={copyInvoiceToDelivery}
                          lockB2BSwitch={true}
                          isPanCustomer={isPanCustomer}
                        />
                      )}

                      {activeTabName.toLowerCase() === "contact" && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "1rem",
                            gap: "12px",
                            flexWrap: "wrap",
                          }}
                        >
                          <CustomerOperationalToggles
                            retailCustomerOperationalSettings={retailCustomerOperationalSettings}
                            isViewMode={isViewMode}
                            isPanCustomer={isPanCustomer}
                            setIsPanCustomer={setIsPanCustomer}
                            panCustomerInfo={panCustomerInfo}
                            setPanCustomerInfo={setPanCustomerInfo}
                            isNriBilling={isNriBilling}
                            setIsNriBilling={setIsNriBilling}
                            isCreditSale={isCreditSale}
                            setIsCreditSale={setIsCreditSale}
                            setIsPanUpdateMode={setIsPanUpdateMode}
                            clearPanFields={clearPanFields}
                          />

                          {documents?.length > 0 && (
                            <Button
                              style={{
                                padding: "6px 16px",
                                border: "1px solid #2F3856",
                                height: "6vh",
                                background: "#fff",
                                borderRadius: "6px",
                                color: "#2F3856",
                                fontWeight: 500,
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                              }}
                              icon={<EyeOutlined />}
                              onClick={() => setUploadModalVisible(true)}
                            >
                              View Documents
                              <span
                                style={{
                                  background: "#2F3856",
                                  color: "#fff",
                                  borderRadius: "10px",
                                  padding: "1px 7px",
                                  fontSize: "11px",
                                  lineHeight: 1.4,
                                }}
                              >
                                {documents.length}
                              </span>
                            </Button>
                          )}
                        </div>
                      )}

                      <div style={{ overflowX: "hidden" }}>
                        {groupedFields.map((group) => {
                          const isExpanded = group.collapsible ? !switchToggle : true;
                          return (
                            <div key={group.groupId}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  marginTop: "2vh",
                                  marginBottom: group.groupName ? "2vh" : "1vh",
                                }}
                              >
                                <div style={{ fontWeight: 600, fontSize: "18px", color: "#0F0718" }}>{group.groupName}</div>
                                {group.collapsible && (
                                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <Switch
                                      checked={switchToggle}
                                      onChange={(checked) => {
                                        setSwitchToggle(checked);
                                        const currentGroupId = group.groupId;
                                        const currentGroupFields = group.fields || [];

                                        if (!checked) {
                                          const emptyGroupValues = {};
                                          currentGroupFields.forEach((field) => {
                                            const key = toCamelCase(field.name);
                                            emptyGroupValues[key] = "";
                                          });
                                          const finalPayload = { [currentGroupId]: emptyGroupValues };
                                          customerForm.setFieldsValue(finalPayload);
                                          setFormDataCache((prev) => ({
                                            ...prev,
                                            [activeTab]: {
                                              ...(prev[activeTab] || {}),
                                              [currentGroupId]: {
                                                ...((prev[activeTab] || {})[currentGroupId] || {}),
                                                ...emptyGroupValues,
                                              },
                                            },
                                          }));
                                          return;
                                        }

                                        const allFormValues = customerForm.getFieldsValue();
                                        const copiedValues = {};
                                        const otherGroupsInSameTab = groupedFields.filter((g) => g.groupId !== currentGroupId);
                                        currentGroupFields.forEach((field) => {
                                          const fieldName = field.name.trim();
                                          for (const otherGroup of otherGroupsInSameTab) {
                                            const otherGroupValues = allFormValues[otherGroup.groupId] || {};
                                            if (Object.prototype.hasOwnProperty.call(otherGroupValues, fieldName) && otherGroupValues[fieldName] !== "") {
                                              copiedValues[currentGroupId] = copiedValues[currentGroupId] || {};
                                              copiedValues[currentGroupId][field.name] = otherGroupValues[fieldName];
                                              break;
                                            }
                                          }
                                        });
                                        setTimeout(() => {
                                          customerForm.setFieldsValue(copiedValues);
                                        }, 0);
                                        setFormDataCache((prev) => ({
                                          ...prev,
                                          [activeTab]: { ...(prev[activeTab] || {}), ...copiedValues },
                                        }));
                                      }}
                                    />
                                    <span style={{ fontWeight: 500, fontSize: "14px", color: "#0F0718" }}>{group.description || "Toggle"}</span>
                                  </div>
                                )}
                              </div>

                              {isExpanded && (
                                <>
                                  {group.groupName?.toLowerCase().includes("address") && (
                                    <div style={{ marginBottom: "16px" }}>
                                      {(() => {
                                        const name = group.groupName?.toLowerCase() || "";
                                        const isInvoice = name.includes("invoice") || name.includes("invoic");
                                        const addressList = isInvoice ? selectedB2BCustomer?.invoiceAddresses || [] : selectedB2BCustomer?.deliveryAddresses || [];
                                        const editValue = customerForm?.getFieldValue([group.groupId, isInvoice ? "invoiceAddress" : "deliveryAddress"]);
                                        return (
                                          <>
                                            <label
                                              style={{
                                                fontWeight: 600,
                                                fontSize: "1.05em",
                                                color: "#0F0718",
                                                display: "block",
                                                marginBottom: "8px",
                                              }}
                                            >
                                              Select {group.groupName}
                                            </label>
                                            <Select
                                              placeholder={`Select ${group.groupName}`}
                                              style={{ width: "100%", height: "44px", borderRadius: "6px" }}
                                              disabled={isViewMode}
                                              value={editValue}
                                              onChange={(value) => {
                                                const existingGroupValues = customerForm.getFieldValue(group.groupId) || {};
                                                customerForm.setFieldsValue({
                                                  [group.groupId]: {
                                                    ...existingGroupValues,
                                                    [isInvoice ? "invoiceAddress" : "deliveryAddress"]: value,
                                                  },
                                                });
                                                const selectedAddr = addressList.find((a) => a.s_customer_address_id === value);
                                                fillAddressFields(selectedAddr, group.groupId, isInvoice ? "invoice" : "delivery", selectedB2BCustomer);
                                              }}
                                            >
                                              {addressList.map((addr) => (
                                                <Select.Option key={addr.s_customer_address_id} value={addr.s_customer_address_id}>
                                                  {`${addr.line1 || ""}, ${addr.line2 || ""}, ${addr.city || ""} - ${addr.pincode || ""}`}
                                                </Select.Option>
                                              ))}
                                            </Select>
                                          </>
                                        );
                                      })()}
                                    </div>
                                  )}

                                  <Row gutter={[16, 0]}>
                                    {filterAndSortFields(group.fields).map((field) => {
                                      const isBusinessTab = activeTabName.toLowerCase() === "business";
                                      const isB2BRegistrationEnabled = customerConfigData?.b2bSettings?.enableB2BCustomerRegistration;
                                      const isDisabledField = isBusinessTab && !isB2BRegistrationEnabled;
                                      return (
                                        <CustomerFormField
                                          key={field.id}
                                          field={field}
                                          group={group}
                                          activeTabName={activeTabName}
                                          isViewMode={isViewMode}
                                          isDisabledField={isDisabledField}
                                          isPanCustomer={isPanCustomer}
                                          panCustomerInfo={panCustomerInfo}
                                          gstInfo={gstInfo}
                                          selectedB2BCustomer={selectedB2BCustomer}
                                          isNriBilling={isNriBilling}
                                          isCreditSale={isCreditSale}
                                          isPanUpdateMode={isPanUpdateMode}
                                          formDataCache={formDataCache}
                                          activeTab={activeTab}
                                          customerForm={customerForm}
                                          optionsMap={optionsMap}
                                          handleChange={handleChange}
                                          handleApiEnterValidation={handleApiEnterValidation}
                                          fetchOptions={fetchOptions}
                                          handleFileUpload={handleFileUpload}
                                          normalizeStaticSelectValue={normalizeStaticSelectValue}
                                          documents={documents}
                                          setDocuments={setDocuments}
                                          selectedEditOldCustomer={selectedEditOldCustomer}
                                        />
                                      );
                                    })}
                                  </Row>
                                  {group?.groupName && <hr style={{ border: "none", borderTop: "1px solid #d1d5db", marginBottom: "16px" }} />}
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <CustomerCouponTab
                      cart={cart}
                      activeTabName={activeTabName}
                      processCouponApplication={processCouponApplication}
                      setDisplayAddNewCustomer={setDisplayAddNewCustomer}
                      setDisplayEditOldCustomer={setDisplayEditOldCustomer}
                      setAdditionalInfo={setAdditionalInfo}
                      setShowCouponOtpPopup={setShowCouponOtpPopup}
                      setCouponVerificationData={setCouponVerificationData}
                      loading={loading}
                      setLoading={setLoading}
                    />
                  </Scrollbars>

                  {!isViewMode && (
                    <Row justify="end" style={{ marginTop: "2vh" }}>
                      <Col>
                        <button
                          style={{
                            padding: "6px 16px",
                            border: "1px solid #ccc",
                            height: "6vh",
                            background: "#fff",
                            borderRadius: "6px",
                            marginRight: "12px",
                            cursor: "pointer",
                          }}
                          onClick={() => setDisplayEditOldCustomer(false)}
                        >
                          Cancel
                        </button>
                      </Col>
                      <Col>
                        <button
                          disabled={updatingCustomer}
                          style={{
                            padding: "6px 16px",
                            background: "#2F3856",
                            height: "6vh",
                            color: "#fff",
                            border: "none",
                            borderRadius: "6px",
                            fontWeight: "bold",
                            cursor: updatingCustomer ? "not-allowed" : "pointer",
                            opacity: updatingCustomer ? 0.6 : 1,
                          }}
                          onClick={handleUpdateCustomer}
                        >
                          {updatingCustomer ? "Updating..." : "Update Customer"}
                        </button>
                      </Col>
                    </Row>
                  )}
                </Form>
              </>
            ) : (
              <LegacyEditCustomerModal
                form={form}
                displayEditOldCustomer={displayEditOldCustomer}
                editFlag={editFlag}
                editOldCustomer={editOldCustomer}
                closeCustomerSearch={closeCustomerSearch}
                setDisplayEditOldCustomer={setDisplayEditOldCustomer}
                setEditFlag={setEditFlag}
                cart={cart}
                selectedEditOldCustomer={selectedEditOldCustomer}
                loyalityType={loyalityType}
                enableLoyality={enableLoyality}
                onSwitchChange={onSwitchChange}
                renderThumb={renderThumb}
                renderThumbHorizontalScroll={renderThumbHorizontalScroll}
                posConfig={posConfig}
                open={open}
                showVirtualKeyboard={showVirtualKeyboard}
                keyboardInputFields={keyboardInputFields}
                setKeyboardInputFields={setKeyboardInputFields}
                keyboard={keyboard}
                setOpen={setOpen}
                setInputName={setInputName}
                inputName={inputName}
                editCustomer={editCustomer}
              />
            )}
          </div>
        </Spin>
      </Drawer>
      <Modal open={uploadModalVisible} footer={null} onCancel={() => setUploadModalVisible(false)} title="Customer Documents" width={700}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            maxHeight: "60vh",
            overflowY: "auto",
          }}
        >
          {documents?.map((doc, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                border: "1px solid #E5E7EB",
                borderRadius: 8,
                padding: 12,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <FileOutlined />

                <span>{doc?.name || `Document ${index + 1}`}</span>
              </div>

              <Button type="link" icon={<EyeOutlined />} onClick={() => openDocument(doc?.url || doc?.docUrl)}>
                View
              </Button>
            </div>
          ))}
        </div>
      </Modal>
      <Modal
        open={docViewer.visible}
        onCancel={closeDocViewer}
        footer={null}
        width="85vw"
        style={{ top: 20 }}
        title="Document Preview"
        destroyOnClose
      >
        {docViewer.url && (
          docViewer.isPdf ? (
            <iframe src={docViewer.url} title="Document" style={{ width: "100%", height: "75vh", border: "none" }} />
          ) : (
            <img src={docViewer.url} alt="Document" style={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain", display: "block", margin: "0 auto" }} />
          )
        )}
      </Modal>
    </Fragment>
  );
};

export default EditCustomerDrawer;
